import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import { GETProductsResponse, PATCHProductRequest, PATCHProductResponse, POSTProductRequest, POSTProductResponse } from './types'
import { FINANCIAL_MODEL_SETTINGS_BASE_KEY, REVENUES_BASE_KEY, TOTALS_KEY, createQueryKey, resetGlobalQueryKey } from '@/api/query-keys'

export const PRODUCTS_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`products`]
})

type PaginationParams = {
  page: number
  pageSize: number
}

export const getProducts = async (financialModelId: string, { page, pageSize }: PaginationParams) => {
  const response = await axiosInstance.get<GETProductsResponse>(`/financial-models/${financialModelId}/products`, {
    params: {
      sort_by: `created_at`,
      page,
      page_size: pageSize
    }
  })
  return response.data
}

export const useGetProductsQuery = (financialModelId: string, pagination?: PaginationParams) =>
  useSuspenseQuery({
    queryKey: PRODUCTS_KEY.concat([financialModelId, pagination?.page, pagination?.pageSize].filter((v): v is string | number => v !== undefined)),
    queryFn: () => getProducts(financialModelId, pagination ?? { page: 1, pageSize: 100 })
  })

export const postProduct = async (financialModelId: string, product: POSTProductRequest): Promise<POSTProductResponse> => {
  const response = await axiosInstance.post<POSTProductResponse>(`/financial-models/${financialModelId}/products`, product)
  return response.data
}

export const useCreateProductMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (product: POSTProductRequest) => postProduct(financialModelId, product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVENUES_BASE_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const duplicateProduct = async (financialModelId: string, products: POSTProductRequest[]) => {
  const promises = products.map(product =>
    axiosInstance.post<POSTProductResponse>(`/financial-models/${financialModelId}/products/`, {
      ...product,
      name: `${product.name} - Copy`
    })
  )
  const responses = await Promise.all(promises)
  return responses.map(response => response.data)
}

export const useDuplicateProductMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (products: POSTProductRequest[]) => duplicateProduct(financialModelId, products),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVENUES_BASE_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const patchProduct = async (financialModelId: string, id: string, product: PATCHProductRequest): Promise<PATCHProductResponse> => {
  const response = await axiosInstance.patch<PATCHProductResponse>(`/financial-models/${financialModelId}/products/${id}`, product)
  return response.data
}

export const useUpdateProductMutation = (financialModelId: string, id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (product: PATCHProductRequest) => patchProduct(financialModelId, id, product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVENUES_BASE_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const deleteProduct = async (financialModelId: string, ids: string[]) => {
  const promises = ids.map(id => axiosInstance.delete(`/financial-models/${financialModelId}/products/${id}`))
  const responses = await Promise.all(promises)
  return responses
}

export const useDeleteProductMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => deleteProduct(financialModelId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVENUES_BASE_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}
